import React from 'react';
import { Alert, TextButton } from '@zillow/constellation-10';
import OnboardingLandingPage from '@rental-manager/pages/Onboarding/LandingPage';
import SinglePropertyDashboard from '@rental-manager/pages/Dashboard/SinglePropertyDashboard';
import { PROPERTY_STATUS } from '@rental-manager/constants/OnboardingConstants';
import DashboardModalRoutes from '@rental-manager/pages//Dashboard/routes';
import OnboardingModalRoutes from '@rental-manager/pages//Onboarding/routes';
import REQUEST_STATUS from '@common/constants/requestStatus';
import Skeleton from '@rental-manager/components/Skeleton';
import queryString from 'query-string';
import './index.scss';
import type { IClusterSettingsModel, IConfigModel, ILocationModel } from '@rental-manager/models/Types';

interface Props {
  baseUrl: string;
  listing: { [key: string]: any };
  location: ILocationModel;
  propertyId: string;
  propertyStore: { [key: string]: any };
  onboarding: { [key: string]: any };
  config: IConfigModel;
  clusterSettings: IClusterSettingsModel;
}
type State = {
  hasLoaded: boolean;
};

class HomePage extends React.Component<Props, State> {
  state = { hasLoaded: false };

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (!state.hasLoaded && props.propertyStore.requestStatus === REQUEST_STATUS.SUCCESS) {
      return { hasLoaded: true };
    }
    return null;
  }

  renderSuccess = (): React.ReactNode => {
    const { baseUrl, listing, propertyId, onboarding, propertyStore, config, clusterSettings, location } = this.props;

    const searchString = location.search;
    const queryParams = queryString.parse(searchString);
    const collectMoveInCosts = queryParams.collectMoveInCosts === 'true';
    const acceptTerms = onboarding[propertyId]?.acceptTerms || queryParams.acceptTerms === 'true';
    const currentTerm = propertyStore?.currentTerm || {};
    const nextTerm = propertyStore?.nextTerm || {};
    const landlordStatus = propertyStore?.landlordStatus || '';
    const termEnded = propertyStore?.currentTerm?.termEnded || false;
    const propertyStatus = propertyStore?.currentTerm?.propertyStatus || '';
    const nextTermPropertyStatus = propertyStore?.nextTerm?.propertyStatus || '';
    const canCreateOneTimePaymentRequest =
      currentTerm.canCreateOneTimePaymentRequest || nextTerm.canCreateOneTimePaymentRequest;

    if (termEnded || propertyStatus === PROPERTY_STATUS.TENANT_INFO_SET) {
      return (
        <div data-panda-theme="legacy-zillow">
          <SinglePropertyDashboard
            baseUrl={baseUrl}
            listing={listing}
            propertyId={propertyId}
            landlordStatus={landlordStatus}
            config={config}
            clusterSettings={clusterSettings}
          />
          <DashboardModalRoutes
            baseUrl={baseUrl}
            listing={listing}
            propertyId={propertyId}
            landlordStatus={landlordStatus}
            config={config}
            clusterSettings={clusterSettings}
            showTermEndedRoutes={termEnded}
            canCreateOneTimePaymentRequest={canCreateOneTimePaymentRequest}
            nextTermPropertyStatus={nextTermPropertyStatus}
          />
        </div>
      );
    }

    return (
      <div data-panda-theme="zillow">
        <OnboardingLandingPage
          baseUrl={baseUrl}
          listing={listing}
          propertyId={propertyId}
          propertyStatus={propertyStatus}
          config={config}
          collectMoveInCosts={collectMoveInCosts}
        />
        <OnboardingModalRoutes
          baseUrl={baseUrl}
          listing={listing}
          propertyId={propertyId}
          acceptTerms={acceptTerms}
          landlordStatus={landlordStatus}
          config={config}
          clusterSettings={clusterSettings}
        />
      </div>
    );
  };

  renderFail(): React.ReactNode {
    return (
      <Alert
        tone="critical"
        actionButton={
          <TextButton asChild>
            <a href="/rental-manager">Go to Rental Manager</a>
          </TextButton>
        }
      >
        We couldn’t load your payments. Try refreshing the page. If that doesn’t work, check back in a few minutes.
      </Alert>
    );
  }

  renderSkeleton = (): React.ReactNode => <Skeleton />;

  render(): React.ReactNode {
    const { propertyStore } = this.props;
    const { requestStatus } = propertyStore;

    // Refetches (e.g. getLandlordStatus after a wizard step saves) keep the loaded page
    // mounted instead of flashing the skeleton and remounting the onboarding modals.
    if (requestStatus === REQUEST_STATUS.SUCCESS || (this.state.hasLoaded && requestStatus !== REQUEST_STATUS.FAIL)) {
      return this.renderSuccess();
    }

    if (requestStatus === REQUEST_STATUS.FAIL) {
      return this.renderFail();
    }

    return this.renderSkeleton();
  }
}

export default HomePage;
